/**
 * ShareholderLevelsTable - the desktop Table view of the shareholder structure (Table.dc.html).
 *
 * One table from the ultimate owners down to the target: the owner levels ('Level 3', 'Level 2',
 * 'Level 1 · direct shareholders', by shortest distance to the target; largest share first, the
 * aggregate last), the highlighted target row, then its subsidiaries when the filters include
 * them. Columns: Shareholder (kind tile and name), Location, Role (the badges), Ownership, Share in
 * target, Sales (€M), Employees, Born. Below its minimum width (a window narrower than about
 * 1265px) the table scrolls sideways inside its own box; the page itself never scrolls sideways.
 * Rows use the card models, chips, badges and figures of the graph and the phone list, so all
 * three phrase every node identically. No React Flow and no layout engine run here.
 *
 * Levels use the shortest distance, so a holder with a direct and an indirect stake is in Level 1
 * although the graph (longest path) draws it one row higher. Known limits: a company that is both
 * owner and subsidiary (a circular holding) is listed once, as owner, without its 'held by the
 * target' stake; subsidiaries the target does not hold directly are not in the graph, so not here.
 *
 * @module ShareholderLevelsTable
 */
import { memo, useCallback, useId, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { CardBadges, HiddenOwnersButton, KindSlot, chipClass } from './graph/CompactShareholderRow';
import { CardFigureValue, FIGURE_LABELS, FigureTooltip } from './graph/CardFigures';
import { DEFAULT_GRAPH_CONTEXT, GraphContext, useGraphContext } from './graph/GraphContext';
import { GRAPH_COPY, NODE_KIND, NOT_APPLICABLE_SHARE } from './graph/graphConstants';
import { keyedLines } from './graph/graphCardModel';
import NoOwnersMessage from './graph/NoOwnersMessage';
import { buildLevelSections, LEVEL_SECTION } from './graph/shareholderLevels';
import HiddenUltimatesNote from './HiddenUltimatesNote';
import IndependentShareholderNote from './IndependentShareholderNote';
import LocationText from './LocationText';
import { useRestoreNodeFocus } from './useRestoreNodeFocus';
import { isBrowserLinkClick, openInNewTabOnModifier } from '../../../../utils/openInNewTab';
import { PUNCTUATION } from '../../../../utils/constants/notation';

// Column order of Table.dc.html; each key names its <col> class (sg-levels-col--<key>).
const COLUMNS = [
  { key: 'name', label: 'Shareholder' },
  { key: 'location', label: 'Location' },
  { key: 'role', label: 'Role' },
  { key: 'ownership', label: 'Ownership' },
  { key: 'share', label: 'Share in target' },
  { key: 'sales', label: FIGURE_LABELS.revenue },
  { key: 'employees', label: FIGURE_LABELS.employees },
  { key: 'born', label: 'Born' }
];

const isPerson = (node) => node.card.entityType === 'person';

// A navigable name links to its page like the popover's View details: the anchor keeps middle
// click, Shift/Alt-click and the browser's context menu; a plain or Cmd/Ctrl click is routed
// here, so the browser never navigates as well.
const NodeName = ({ node, getNodeHref, onViewNodeDetails, onOpenNodeInNewTab }) => {
  const { card } = node;
  const href = card.navigable ? getNodeHref?.(node.raw, node) : null;
  if (!href) {
    return (
      <span className="sg-levels-name" title={card.name}>
        {card.name}
      </span>
    );
  }
  const handleClick = (event) => {
    if (isBrowserLinkClick(event)) return;
    event.preventDefault();
    openInNewTabOnModifier(
      () => onOpenNodeInNewTab?.(node.raw, node),
      () => onViewNodeDetails?.(node.raw, node)
    )(event);
  };
  return (
    <a className="sg-levels-name" href={href} title={card.name} onClick={handleClick}>
      {card.name}
    </a>
  );
};

NodeName.propTypes = {
  node: PropTypes.object.isRequired,
  getNodeHref: PropTypes.func,
  onViewNodeDetails: PropTypes.func,
  onOpenNodeInNewTab: PropTypes.func
};

// The row header: the kind tile beside the name, nothing else (the location and the badges have
// their own columns). Out of the tab order (tabIndex -1) but focusable: the focus target after
// '+N owners above'.
const NameCell = ({ node, getNodeHref, onViewNodeDetails, onOpenNodeInNewTab }) => (
  <th scope="row" tabIndex={-1} data-node-id={node.id}>
    <div className="sg-levels-name-cell">
      <KindSlot entityType={node.card.entityType} />
      <NodeName
        node={node}
        getNodeHref={getNodeHref}
        onViewNodeDetails={onViewNodeDetails}
        onOpenNodeInNewTab={onOpenNodeInNewTab}
      />
    </div>
  </th>
);

NameCell.propTypes = {
  node: PropTypes.object.isRequired,
  getNodeHref: PropTypes.func,
  onViewNodeDetails: PropTypes.func,
  onOpenNodeInNewTab: PropTypes.func
};

// Empty for the aggregate; n/a without a location, like the Subsidiaries table (the tile already
// shows the kind).
const LocationCell = ({ node }) => {
  if (node.kind === NODE_KIND.AGGREGATE) return <td className="sg-levels-location" />;
  const { locationParts } = node.card;
  return (
    <td className="sg-levels-location">
      {locationParts.length ? <LocationText parts={locationParts} /> : PUNCTUATION.EMPTY_VALUE}
    </td>
  );
};

LocationCell.propTypes = {
  node: PropTypes.object.isRequired
};

// Target, Ultimate owner, General partner (a node can carry the last two); empty for the
// aggregate and the subsidiaries, which have no badges.
const RoleCell = ({ node }) => (
  <td className="sg-levels-role">
    <CardBadges badges={node.card.badges} />
  </td>
);

RoleCell.propTypes = {
  node: PropTypes.object.isRequired
};

// The holds lines, then '+N owners above' of a collapsed owner or the aggregate's 'Show all'.
const OwnershipCell = ({ node, onToggleNodeCollapsed, onShowAllFiltered }) => (
  <td className="sg-levels-ownership">
    {keyedLines(node.card.holdsLines).map(({ key, line }) => (
      <span key={key} className="sg-levels-line">
        {line}
      </span>
    ))}
    {node.hiddenOwnerCount > 0 && onToggleNodeCollapsed && (
      <HiddenOwnersButton node={node} onToggleCollapsed={onToggleNodeCollapsed} />
    )}
    {node.kind === NODE_KIND.AGGREGATE && onShowAllFiltered && (
      <button type="button" className="sg-link-button" onClick={onShowAllFiltered}>
        {GRAPH_COPY.SHOW_ALL}
      </button>
    )}
  </td>
);

OwnershipCell.propTypes = {
  node: PropTypes.object.isRequired,
  onToggleNodeCollapsed: PropTypes.func,
  onShowAllFiltered: PropTypes.func
};

// Owners and the aggregate only: the target has no share, a subsidiary's is its Ownership line.
// An intermediate owner's '–' stays plain text, without a chip.
const ShareCell = ({ node }) => {
  const { share } = node.card;
  const hasShare = node.kind === NODE_KIND.OWNER || node.kind === NODE_KIND.AGGREGATE;
  if (!hasShare || !share) return <td className="sg-levels-share" />;
  return (
    <td className="sg-levels-share">
      {share === NOT_APPLICABLE_SHARE ? share : <span className={chipClass(node)}>{share}</span>}
    </td>
  );
};

ShareCell.propTypes = {
  node: PropTypes.object.isRequired
};

// Companies, the target and subsidiaries included; persons and the aggregate have no figures.
const FigureCell = ({ node, figure }) => {
  const { figuresStatus } = useGraphContext();
  const hasFigures = !isPerson(node) && node.kind !== NODE_KIND.AGGREGATE;
  return (
    <td className="sg-levels-figure">
      {hasFigures && (
        <CardFigureValue figure={figure} figures={node.card.figures} status={figuresStatus} />
      )}
    </td>
  );
};

FigureCell.propTypes = {
  node: PropTypes.object.isRequired,
  figure: PropTypes.oneOf(['revenue', 'employees']).isRequired
};

const BornCell = ({ node }) => (
  <td className="sg-levels-born">
    {isPerson(node) ? (node.card.birthYear ?? PUNCTUATION.EMPTY_VALUE) : null}
  </td>
);

BornCell.propTypes = {
  node: PropTypes.object.isRequired
};

const LevelRow = ({
  node,
  getNodeHref,
  onViewNodeDetails,
  onOpenNodeInNewTab,
  onToggleNodeCollapsed,
  onShowAllFiltered
}) => (
  <tr
    className={`sg-levels-row sg-levels-row--${node.kind}${node.card.isUltimate ? ' is-ultimate' : ''}`}
  >
    <NameCell
      node={node}
      getNodeHref={getNodeHref}
      onViewNodeDetails={onViewNodeDetails}
      onOpenNodeInNewTab={onOpenNodeInNewTab}
    />
    <LocationCell node={node} />
    <RoleCell node={node} />
    <OwnershipCell
      node={node}
      onToggleNodeCollapsed={onToggleNodeCollapsed}
      onShowAllFiltered={onShowAllFiltered}
    />
    <ShareCell node={node} />
    <FigureCell node={node} figure="revenue" />
    <FigureCell node={node} figure="employees" />
    <BornCell node={node} />
  </tr>
);

LevelRow.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    kind: PropTypes.oneOf(Object.values(NODE_KIND)).isRequired,
    card: PropTypes.object.isRequired
  }).isRequired,
  getNodeHref: PropTypes.func,
  onViewNodeDetails: PropTypes.func,
  onOpenNodeInNewTab: PropTypes.func,
  onToggleNodeCollapsed: PropTypes.func,
  onShowAllFiltered: PropTypes.func
};

// One row group per section; the owner levels and the subsidiaries start with their heading row.
const LevelSection = ({ section, rowProps }) => (
  <tbody>
    {section.heading && (
      <tr>
        <th scope="rowgroup" colSpan={COLUMNS.length} className="sg-levels-group-heading">
          {section.heading}
        </th>
      </tr>
    )}
    {section.nodes.map((node) => (
      <LevelRow key={node.id} node={node} {...rowProps} />
    ))}
  </tbody>
);

LevelSection.propTypes = {
  section: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(LEVEL_SECTION)).isRequired,
    heading: PropTypes.string,
    nodes: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  rowProps: PropTypes.shape({
    getNodeHref: PropTypes.func,
    onViewNodeDetails: PropTypes.func,
    onOpenNodeInNewTab: PropTypes.func,
    onToggleNodeCollapsed: PropTypes.func,
    onShowAllFiltered: PropTypes.func
  }).isRequired
};

/**
 * @param {Object} props
 * @param {Object|null} props.graph - PreparedGraph (null: no structure)
 * @param {'idle'|'loading'|'loaded'|'failed'} [props.figuresStatus]
 * @param {number} [props.percentageFilter] - for the no-owners message
 * @param {string} [props.labelledBy] - id of the card heading that names the table
 * @param {boolean} [props.isIndependent] - no majority shareholder: the note below the table
 * @param {string} [props.companyName] - the company that note names
 * @param {(raw, node) => string} [props.getNodeHref] - href of a node's page
 * @param {(raw, node) => void} [props.onViewNodeDetails] - same-tab navigation of a name
 * @param {(raw, node) => void} [props.onOpenNodeInNewTab] - Ctrl/Cmd+click on a name
 * @param {(nodeId: string) => void} [props.onToggleNodeCollapsed] - '+N owners above'
 * @param {() => void} [props.onShowAllFiltered] - the aggregate row's 'Show all'
 * @param {() => void} [props.onExpandAllOwners] - 'Show all owners' below the table
 * @param {() => void} [props.onOpenFilters] - 'Change filter' in the no-owners message
 */
const ShareholderLevelsTable = ({
  graph = null,
  figuresStatus = 'loaded',
  percentageFilter,
  labelledBy,
  isIndependent = false,
  companyName = '',
  getNodeHref,
  onViewNodeDetails,
  onOpenNodeInNewTab,
  onToggleNodeCollapsed,
  onShowAllFiltered,
  onExpandAllOwners,
  onOpenFilters
}) => {
  const tooltipId = `sg-levels-tooltip-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const contextValue = useMemo(
    () => ({ ...DEFAULT_GRAPH_CONTEXT, tooltipId, figuresStatus }),
    [tooltipId, figuresStatus]
  );
  const sections = useMemo(() => buildLevelSections(graph), [graph]);
  const tableRef = useRef(null);
  const requestFocus = useRestoreNodeFocus(tableRef, graph);
  // '+N owners above', 'Show all' and 'Show all owners' remove themselves: keep focus in the table.
  const toggleCollapsed = useCallback(
    (nodeId) => {
      requestFocus(nodeId);
      onToggleNodeCollapsed(nodeId);
    },
    [requestFocus, onToggleNodeCollapsed]
  );
  const showAll = useCallback(() => {
    requestFocus(null);
    onShowAllFiltered();
  }, [requestFocus, onShowAllFiltered]);
  const expandAllOwners = useCallback(() => {
    requestFocus(null);
    onExpandAllOwners();
  }, [requestFocus, onExpandAllOwners]);

  if (!graph?.nodes?.length) {
    return (
      <div className="sg-levels">
        <output className="sg-levels-message">{GRAPH_COPY.EMPTY}</output>
        {isIndependent && <IndependentShareholderNote companyName={companyName} />}
      </div>
    );
  }

  const rowProps = {
    getNodeHref,
    onViewNodeDetails,
    onOpenNodeInNewTab,
    onToggleNodeCollapsed: onToggleNodeCollapsed ? toggleCollapsed : undefined,
    onShowAllFiltered: onShowAllFiltered ? showAll : undefined
  };

  return (
    <GraphContext.Provider value={contextValue}>
      <div className="sg-levels">
        <p className="sg-levels-hint">{GRAPH_COPY.MOBILE_HINT}</p>
        <NoOwnersMessage
          className="sg-levels-message"
          cause={graph.noOwnersCause}
          percentageFilter={percentageFilter}
          onOpenFilters={onOpenFilters}
        />
        {/* Only the table scrolls sideways (below its minimum width); the notes stay put. */}
        <div className="sg-levels-scroll">
          <table
            className="sg-levels-table"
            aria-labelledby={labelledBy}
            tabIndex={-1}
            ref={tableRef}
          >
            <colgroup>
              {COLUMNS.map((column) => (
                <col key={column.key} className={`sg-levels-col--${column.key}`} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column.key} scope="col">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            {sections.map((section) => (
              <LevelSection key={section.key} section={section} rowProps={rowProps} />
            ))}
          </table>
        </div>
        <HiddenUltimatesNote
          className="sg-levels-note"
          count={graph.hiddenUltimateCount}
          onExpandAllOwners={onExpandAllOwners ? expandAllOwners : undefined}
        />
        {isIndependent && <IndependentShareholderNote companyName={companyName} />}
        <FigureTooltip id={tooltipId} />
      </div>
    </GraphContext.Provider>
  );
};

ShareholderLevelsTable.propTypes = {
  graph: PropTypes.object,
  figuresStatus: PropTypes.oneOf(['idle', 'loading', 'loaded', 'failed']),
  percentageFilter: PropTypes.number,
  labelledBy: PropTypes.string,
  isIndependent: PropTypes.bool,
  companyName: PropTypes.string,
  getNodeHref: PropTypes.func,
  onViewNodeDetails: PropTypes.func,
  onOpenNodeInNewTab: PropTypes.func,
  onToggleNodeCollapsed: PropTypes.func,
  onShowAllFiltered: PropTypes.func,
  onExpandAllOwners: PropTypes.func,
  onOpenFilters: PropTypes.func
};

// Memoised: every prop is stable between page renders that do not concern the table (e.g. each
// keystroke in the filter panel), so those renders stop here instead of re-rendering every row.
export default memo(ShareholderLevelsTable);
